/**
 * src/pages/admin/AdminNotifications.jsx
 *
 * Live monitor for push notifications emitted by Scrimly Cloud Functions.
 * Reads collectionGroup('notifications') — every doc written by a server-
 * side notification path lands here.
 *
 * Post-pivot cleanup (R3): matching-era types (scrimmage_*, interest_*,
 * local_scrimmage, match_confirmed/cancelled, score_review, opportunity_*)
 * stripped from TYPE_META + filter chips + KPI. Current TYPE_META covers
 * the known Scrimly emitters; anything not in the table falls back to the
 * "Other" bucket with the raw type string rendered as-is.
 *
 * See features/admin-notifications-cleanup/01-spec.md for the scope.
 */

import { useState, useEffect, useCallback } from 'react';
import { db } from '@/firebase/firebase';
import {
  collectionGroup,
  query,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

// ─── Type metadata ─────────────────────────────────────────────────────
// Add new types here as they're emitted by Scrimly functions. The "Other"
// bucket renders unrecognized types with their raw string + neutral pill,
// so missing labels degrade gracefully.

const TYPE_META = {
  // Chat / messaging
  message:                    { label: '💬 Message',        color: 'bg-purple-100 text-purple-700 border-purple-200' },
  new_message:                { label: '💬 Message',        color: 'bg-purple-100 text-purple-700 border-purple-200' },
  chat_message:               { label: '💬 Message',        color: 'bg-purple-100 text-purple-700 border-purple-200' },
  new_chat_message:           { label: '💬 Message',        color: 'bg-purple-100 text-purple-700 border-purple-200' },
  // Weather + ops
  weather_alert:              { label: '⛈️ Weather',         color: 'bg-amber-100 text-amber-700 border-amber-200' },
  // Payments + billing
  payment_reminder:           { label: '💳 Payment',         color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  installment_due:            { label: '💳 Payment',         color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  // Registration
  registration_confirmation:  { label: '📝 Registration',    color: 'bg-lime-100 text-lime-800 border-lime-200' },
  // Coach broadcasts
  broadcast:                  { label: '📣 Broadcast',       color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
};

const FILTER_GROUPS = [
  { key: 'all',       label: 'All' },
  { key: 'messages',  label: '💬 Messages' },
  { key: 'weather',   label: '⛈️ Weather' },
  { key: 'payment',   label: '💳 Payment' },
  { key: 'broadcast', label: '📣 Broadcast' },
  { key: 'other',     label: 'Other' },
];

// ─── Helpers ──────────────────────────────────────────────────────────

function getType(n) {
  return ((n.data?.type || n.type || '')).toLowerCase().trim();
}

function getMeta(type) {
  return TYPE_META[type] || { label: type || 'Unknown', color: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
}

function toMs(ts) {
  if (!ts) return 0;
  if (ts instanceof Timestamp) return ts.toMillis();
  if (ts?.seconds) return ts.seconds * 1000;
  return Number(ts) || 0;
}

function timeAgo(ts) {
  const diff = Date.now() - toMs(ts);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function fmtTime(ts) {
  const ms = toMs(ts);
  if (!ms) return '—';
  return new Date(ms).toLocaleString('en-US', {
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function passesFilter(n, filter) {
  const type = getType(n);
  switch (filter) {
    case 'messages':  return type.includes('message');
    case 'weather':   return type.includes('weather');
    case 'payment':   return type.includes('payment') || type.includes('installment');
    case 'broadcast': return type.includes('broadcast');
    case 'other':     return !TYPE_META[type];
    default:          return true;
  }
}

// ─── Main Component ────────────────────────────────────────────────────

export default function AdminNotifications() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [notifications, setNotifications] = useState([]);
  const [userNames, setUserNames]         = useState({});
  const [loading, setLoading]             = useState(true);
  const [spinning, setSpinning]           = useState(false);
  const [filter, setFilter]               = useState('all');
  const [range, setRange]                 = useState(24); // hours

  const fetchData = useCallback(async () => {
    setSpinning(true);
    try {
      const cutoffMs = Date.now() - range * 60 * 60 * 1000;

      // Try ordered query first; fall back to unordered if index missing
      let snap;
      try {
        snap = await getDocs(
          query(collectionGroup(db, 'notifications'), orderBy('createdAt', 'desc'), limit(600)),
        );
      } catch {
        try {
          snap = await getDocs(
            query(collectionGroup(db, 'notifications'), orderBy('timestamp', 'desc'), limit(600)),
          );
        } catch {
          snap = await getDocs(query(collectionGroup(db, 'notifications'), limit(600)));
        }
      }

      const all = snap.docs.map((d) => ({
        _path: d.ref.path,
        _id:   d.id,
        ...d.data(),
      }));

      // Filter to time window and sort newest first
      const inWindow = all
        .filter((n) => toMs(n.createdAt || n.timestamp) > cutoffMs)
        .sort((a, b) => toMs(b.createdAt || b.timestamp) - toMs(a.createdAt || a.timestamp));

      setNotifications(inWindow);

      // Resolve display names for unique user IDs
      const uids = [...new Set(inWindow.map((n) => n.userId).filter(Boolean))];
      if (uids.length) {
        const batches = [];
        for (let i = 0; i < uids.length; i += 30) batches.push(uids.slice(i, i + 30));

        const nameMap = {};
        await Promise.all(
          batches.flatMap((batch) =>
            batch.map(async (uid) => {
              try {
                const snap = await getDoc(doc(db, 'users', uid));
                if (snap.exists()) {
                  const d = snap.data();
                  nameMap[uid] = d.displayName || d.email || uid.slice(0, 8);
                }
              } catch { /* ignore */ }
            }),
          ),
        );
        setUserNames((prev) => ({ ...prev, ...nameMap }));
      }
    } catch (err) {
      console.error('[AdminNotifications]', err);
    } finally {
      setLoading(false);
      setSpinning(false);
    }
  }, [range]);

  useEffect(() => {
    if (blocked) return;
    fetchData();
  }, [blocked, fetchData]);

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view the notification monitor.
          </p>
        </div>
      </div>
    );
  }

  // ── Derived stats ─────────────────────────────────────────────────────

  const displayed = notifications.filter((n) => passesFilter(n, filter));

  const total        = notifications.length;
  const uniqueRecips = new Set(notifications.map((n) => n.userId || n.teamId).filter(Boolean)).size;
  const readCount    = notifications.filter((n) => n.isRead).length;
  const readRate     = total > 0 ? Math.round((readCount / total) * 100) : 0;
  const unreadCount  = total - readCount;

  const rangeLabel = range === 168 ? '7d' : `${range}h`;

  const KPI_CARDS = [
    { label: `Sent (last ${rangeLabel})`, value: total },
    { label: 'Unique Recipients',         value: uniqueRecips },
    { label: 'Read Rate',                 value: `${readRate}%` },
    { label: 'Unread',                    value: unreadCount },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-5">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1
            className="text-3xl font-bold uppercase tracking-tight text-zinc-900"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Notification Monitor
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Every push notification written to Firestore — if it's here, FCM attempted delivery.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-zinc-200 overflow-hidden text-xs font-semibold">
            {[
              { h: 24,  label: '24h' },
              { h: 48,  label: '48h' },
              { h: 168, label: '7d'  },
            ].map(({ h, label }) => (
              <button
                key={h}
                type="button"
                onClick={() => setRange(h)}
                className={`px-3 py-1.5 transition-colors ${
                  range === h
                    ? 'bg-lime-400 text-zinc-900'
                    : 'text-zinc-600 hover:bg-zinc-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={fetchData}
            title="Refresh"
            disabled={spinning}
            className="inline-flex items-center justify-center p-2 rounded-md border border-zinc-200 text-zinc-500 hover:bg-zinc-50 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${spinning ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {KPI_CARDS.map(({ label, value }) => (
          <div key={label} className="rounded-2xl border border-zinc-200 bg-white px-4 py-3.5">
            <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</p>
            <p
              className="mt-1 text-3xl font-bold tabular-nums text-zinc-900"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {loading ? '—' : value}
            </p>
          </div>
        ))}
      </div>

      {/* ── Info banner ── */}
      <div className="rounded-md border border-lime-200 bg-lime-50 px-4 py-3 text-xs text-zinc-700 flex items-start gap-2">
        <span className="mt-0.5 shrink-0">ℹ️</span>
        <span>
          <strong>How this works:</strong> Each row is a Firestore notification doc. When a coach has
          an FCM token registered, the Cloud Function fires a push to their device.{' '}
          <strong>Read</strong> status turns green when the coach taps the notification and it's
          marked read in the app. FCM delivery to the actual device is handled by Google and isn't
          separately logged here.
        </span>
      </div>

      {/* ── Filter tabs ── */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTER_GROUPS.map(({ key, label }) => {
          const count = key === 'all' ? total : notifications.filter((n) => passesFilter(n, key)).length;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                filter === key
                  ? 'bg-lime-400 border-lime-500 text-zinc-900'
                  : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
              }`}
            >
              {label}
              <span className={`ml-1.5 text-[10px] ${filter === key ? 'opacity-80' : 'text-zinc-400'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── Table ── */}
      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-16 text-center">
            <ArrowPathIcon className="inline-block h-6 w-6 text-lime-500 animate-spin mb-3" />
            <p className="text-sm text-zinc-400">Loading notifications…</p>
          </div>
        ) : displayed.length === 0 ? (
          <div className="p-16 text-center text-zinc-400 text-sm">
            No notifications in this time window.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-400">Time</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-400">Recipient</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-400">Type</th>
                  <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest text-zinc-400">Notification</th>
                  <th className="px-4 py-3 text-center text-[10px] font-bold uppercase tracking-widest text-zinc-400">Read</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {displayed.slice(0, 250).map((n) => {
                  const type      = getType(n);
                  const meta      = getMeta(type);
                  const ts        = n.createdAt || n.timestamp;
                  const recipient = userNames[n.userId]
                    || (n.teamId ? `Team …${n.teamId.slice(-5)}` : n.userId ? `…${n.userId.slice(-5)}` : '—');

                  return (
                    <tr key={n._path} className="hover:bg-zinc-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-xs font-semibold text-zinc-700">{timeAgo(ts)}</p>
                        <p className="text-[10px] text-zinc-400 font-mono">{fmtTime(ts)}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-medium text-zinc-800">{recipient}</span>
                        {n.userId && (
                          <p className="text-[10px] text-zinc-400 font-mono">…{n.userId.slice(-8)}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.color}`}>
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <p className="text-xs font-semibold text-zinc-800 truncate">{n.title || '—'}</p>
                        <p className="text-[11px] text-zinc-400 truncate mt-0.5">{n.body || ''}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {n.isRead ? (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-lime-100 text-lime-700 text-xs font-bold">✓</span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-zinc-100 text-zinc-400 text-xs">–</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer totals */}
        {!loading && displayed.length > 0 && (
          <div className="px-4 py-2.5 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between text-xs text-zinc-500">
            <span>
              Showing <strong className="text-zinc-700">{Math.min(displayed.length, 250)}</strong> of{' '}
              <strong className="text-zinc-700">{displayed.length}</strong> notifications
            </span>
            <span>
              <strong className="text-zinc-700">{readCount}</strong> read /{' '}
              <strong className="text-zinc-700">{unreadCount}</strong> unread
            </span>
          </div>
        )}
      </div>

    </div>
  );
}
